import { Formik, Form } from "formik";
import { usePostHog } from "posthog-js/react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { toast } from "@volca/ui/lib/toast";
import { useGetBusiness } from "@/business/query";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business/use-business";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { useGetAccountSummary } from "@/internal-transactions/query";
import { useSendPayout } from "@/payouts/query";
import { REFERRAL_COLUMN_TO_STATUS } from "@/referrals/constants";
import { useUpdateReferral } from "@/referrals/query";
import { IReferral } from "@/referrals/referral";
import { PayoutFields } from "./payout-fields";
import { PayoutNotices } from "./payout-notices";
import { createPayoutSchema, payoutInitialValues } from "./schema";

interface PayoutModalProps {
  onOpenChange: (referral: IReferral | null) => void;
  referral: IReferral | null;
  onFinalPayout?: (referralId: string) => void;
}

export const PayoutModal = ({
  onOpenChange,
  referral,
  onFinalPayout,
}: PayoutModalProps) => {
  const posthog = usePostHog();
  const { businessId } = useBusiness();

  const { data: business } = useGetBusiness(businessId);
  const { data: accountSummary, isLoading: isAccountSummaryLoading } =
    useGetAccountSummary(businessId);

  const { id: referralId = "" } = referral || {};
  const { mutate: sendPayout } = useSendPayout(referralId);
  const { mutate: updateReferral } = useUpdateReferral(businessId);

  const isClosed = referral?.status === REFERRAL_COLUMN_TO_STATUS.closed;
  const requiresDealSize = !referral?.deal_size;

  return (
    <Dialog
      open={!!referral}
      onOpenChange={(open) => onOpenChange(open ? referral : null)}
    >
      <DialogContent>
        <DialogDescription className="sr-only">
          Send payout to customer
        </DialogDescription>
        <Formik
          initialValues={payoutInitialValues}
          validationSchema={createPayoutSchema(requiresDealSize)}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            if (requiresDealSize && values.deal_size) {
              const parsedDealSize = parseMaskedDollarInput(values.deal_size);
              if (parsedDealSize !== null && parsedDealSize > 0) {
                updateReferral({
                  id: referralId,
                  updates: { deal_size: parsedDealSize },
                });
              }
            }

            sendPayout(
              {
                ...values,
                amount: Math.round(
                  (parseMaskedDollarInput(values.amount) ?? 0) * 100,
                ),
              },
              {
                onSuccess: () => {
                  onOpenChange(null);
                  if (values.final_payout) {
                    onFinalPayout?.(referralId);
                  }
                  posthog?.capture("payout_sent", {
                    referral_id: referralId,
                    amount: values.amount,
                    final_payout:
                      referral?.status === REFERRAL_COLUMN_TO_STATUS.closed,
                  });
                  toast.success("Payout sent");
                  setSubmitting(false);
                },
                onError: (error) => {
                  posthog?.capture("payout_failed", {
                    referral_id: referralId,
                    amount: values.amount,
                    final_payout:
                      referral?.status === REFERRAL_COLUMN_TO_STATUS.closed,
                  });
                  toast.error("Failed to send payout", {
                    description: getErrorMessage(error),
                  });
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({ isSubmitting, isValid, values }) => {
            const walletBalance = accountSummary?.wallet_balance;
            const payoutAmount = parseMaskedDollarInput(values.amount) ?? 0;
            const hasInsufficientFunds =
              !isAccountSummaryLoading &&
              walletBalance != null &&
              payoutAmount > Number(walletBalance);
            const shouldDisableSubmit =
              isSubmitting ||
              !isValid ||
              isAccountSummaryLoading ||
              hasInsufficientFunds;

            return (
              <Form className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>Send payout</DialogTitle>
                </DialogHeader>
                <div className="-mx-4 max-h-[70vh] overflow-y-auto px-4">
                  <div>
                    <PayoutNotices
                      businessId={businessId}
                      showNotClosedWarning={!!referral && !isClosed}
                      showInsufficientFunds={
                        hasInsufficientFunds &&
                        !!values.amount &&
                        !isAccountSummaryLoading
                      }
                      isAccountSummaryLoading={isAccountSummaryLoading}
                    />
                    <PayoutFields
                      referral={referral}
                      requiresDealSize={requiresDealSize}
                      maxPayout={business?.max_payout}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => onOpenChange(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={shouldDisableSubmit}
                    loading={isSubmitting}
                    loadingLabel="Send Payment, in progress"
                  >
                    Send Payment
                  </Button>
                </DialogFooter>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
